import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Banknote,
    Plus,
    Edit,
    Trash2,
    CheckCircle,
    X,
    Percent,
    Calendar,
    TrendingUp,
    FileText,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
    fetchAvanceRequest,
    createAvanceRequest,
    updateAvanceRequest,
    deleteAvanceRequest,
} from '../../../../redux/actions/avanceActions';
import '../parametresStyles/ParamNF.css';

const ParamAvances = () => {
    const dispatch = useDispatch();
    const { avances, loading, error } = useSelector((state) => state.avance || {
        avances: [],
        loading: false,
        error: null,
    });

    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [activeTab, setActiveTab] = useState("avances");
    const [formData, setFormData] = useState({
        type: '',
        description: '',
        categorie: 'avance',
        ancienneteMinimale: '',
        typeContrat: '',
        nbMensualiteRemboursement: '',
        tauxInteret: '',
        modeRetenue: 'salaire',
        autreModeRetenue: '',
        remboursementAnticipe: true,
        typeJustificatif: '',
        obligatoire: false,
        actif: true,
        workflowLevel: 'manager',

    });

    const categorieOptions = [
        { value: 'avance', label: 'Avance' },
        { value: 'pret', label: 'Prêt' },
    ];

    const modeRetenueOptions = [
        { value: 'salaire', label: 'Salaire' },
        { value: 'prime', label: 'Prime' },
        { value: 'autre', label: 'Autre' },
    ];
    const workflowOptions = [
        { value: 'manager', label: 'Manager' },
        { value: 'admin', label: 'Admin RH' },
        { value: 'both', label: 'Les deux' },
    ];

    const typeJustificatifOptions = [
        'FACTURE',
        'DEVIS',
        'ATTESTATION',
        'CONTRAT',
        'AUCUN',
    ];

    const typeContratOptions = [
        'CDI',
        'CDD',
        'TOUS',
    ];

    useEffect(() => {
        dispatch(fetchAvanceRequest());
    }, [dispatch]);

    const tabs = useMemo(
        () => [
            { id: "avances", label: "Avances & Prêts", icon: Banknote },
            { id: "plafonds", label: "Plafonds", icon: TrendingUp },
        ],
        []
    );

    const openModal = (avance = null) => {
        if (avance) {
            setEditItem(avance);
            setFormData({
                type: avance.type || '',
                description: avance.description || '',
                categorie: avance.categorie || 'avance',
                ancienneteMinimale: avance.ancienneteMinimale || '',
                typeContrat: avance.typeContrat || '',
                nbMensualiteRemboursement: avance.nbMensualiteRemboursement || '',
                tauxInteret: avance.tauxInteret || '',
                modeRetenue: avance.modeRetenue || 'salaire',
                autreModeRetenue: avance.autreModeRetenue || '',
                remboursementAnticipe: avance.remboursementAnticipe ?? true,
                typeJustificatif: avance.typeJustificatif || '',
                obligatoire: avance.obligatoire || false,
                actif: avance.actif ?? true,
                workflowLevel: avance.workflowLevel || 'manager',

            });
        } else {
            setEditItem(null);
            setFormData({
                type: '',
                description: '',
                categorie: 'avance',
                ancienneteMinimale: '',
                typeContrat: '',
                nbMensualiteRemboursement: '',
                tauxInteret: '',
                modeRetenue: 'salaire',
                autreModeRetenue: '',
                remboursementAnticipe: true,
                typeJustificatif: '',
                obligatoire: false,
                actif: true,
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setFormData({
            type: '',
            description: '',
            categorie: 'avance',
            ancienneteMinimale: '',
            typeContrat: '',
            nbMensualiteRemboursement: '',
            tauxInteret: '',
            modeRetenue: 'salaire',
            autreModeRetenue: '',
            remboursementAnticipe: true,
            typeJustificatif: '',
            obligatoire: false,
            actif: true,
        });
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.type.trim() || !formData.categorie) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Le type et la catégorie sont obligatoires',
                confirmButtonColor: '#204385',
            });
            return;
        }

        // Validation pour le champ "autre_mode_retenue"
        if (formData.modeRetenue === 'autre' && !formData.autreModeRetenue.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Veuillez préciser le mode de retenue',
                confirmButtonColor: '#204385',
            });
            return;
        }

        const payload = {
            ...formData,
            ancienneteMinimale: formData.ancienneteMinimale ? parseInt(formData.ancienneteMinimale) : null,
            nbMensualiteRemboursement: formData.nbMensualiteRemboursement ? parseInt(formData.nbMensualiteRemboursement) : null,
            tauxInteret: formData.tauxInteret ? parseFloat(formData.tauxInteret) : null,
            typeJustificatif: formData.typeJustificatif || null,
            typeContrat: formData.typeContrat || null,
        };

        if (editItem) {
            dispatch(updateAvanceRequest(editItem.id, payload));
        } else {
            dispatch(createAvanceRequest(payload));
        }
        closeModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Voulez-vous vraiment supprimer cette avance/prêt ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#204385',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteAvanceRequest(id));
            }
        });
    };

    const toggleActif = (avance) => {
        dispatch(
            updateAvanceRequest(avance.id, {
                ...avance,
                actif: !avance.actif,
            })
        );
    };

    const getCategorieLabel = (categorie) => {
        const option = categorieOptions.find(opt => opt.value === categorie);
        return option ? option.label : categorie;
    };

    const getModeRetenueLabel = (mode) => {
        const option = modeRetenueOptions.find(opt => opt.value === mode);
        return option ? option.label : mode;
    };

    const renderModal = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? 'Modifier une avance/prêt' : 'Ajouter une avance/prêt'}
                        </div>
                        <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer" disabled={loading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="pcForm">
                            <div className="pcField">
                                <label>Type *</label>
                                <input
                                    type="text"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    placeholder="Ex: Avance sur salaire, Prêt logement..."
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Catégorie *</label>
                                <div className="pcRadioGroup">
                                    {categorieOptions.map((option) => (
                                        <label key={option.value} className="pcRadioLabel">
                                            <input
                                                type="radio"
                                                name="categorie"
                                                value={option.value}
                                                checked={formData.categorie === option.value}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pcField">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Description détaillée de l'avance/prêt"
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Ancienneté minimale (mois)</label>
                                <input
                                    type="number"
                                    name="ancienneteMinimale"
                                    value={formData.ancienneteMinimale}
                                    onChange={handleChange}
                                    min="0"
                                    placeholder="Ex: 6"
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Type de contrat</label>
                                <select
                                    name="typeContrat"
                                    value={formData.typeContrat}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Tous les contrats</option>
                                    {typeContratOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="pcField">
                                <label>Nombre de mensualités</label>
                                <input
                                    type="number"
                                    name="nbMensualiteRemboursement"
                                    value={formData.nbMensualiteRemboursement}
                                    onChange={handleChange}
                                    min="1"
                                    placeholder="Ex: 12"
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Taux d'intérêt (%)</label>
                                <input
                                    type="number"
                                    name="tauxInteret"
                                    value={formData.tauxInteret}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="Ex: 2.5"
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Mode de retenue *</label>
                                <select
                                    name="modeRetenue"
                                    value={formData.modeRetenue}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    {modeRetenueOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.modeRetenue === 'autre' && (
                                <div className="pcField">
                                    <label>Préciser le mode de retenue *</label>
                                    <input
                                        type="text"
                                        name="autreModeRetenue"
                                        value={formData.autreModeRetenue}
                                        onChange={handleChange}
                                        placeholder="Ex: Retenue annuelle, Sur 13ème mois..."
                                        disabled={loading}
                                        required
                                    />
                                    <small style={{
                                        color: '#6b7280',
                                        fontSize: '0.875rem',
                                        marginTop: '4px',
                                        display: 'block'
                                    }}>
                                        Ce champ sera stocké dans "autre_mode_retenue"
                                    </small>
                                </div>
                            )}

                            <div className="pcField">
                                <label>Type de justificatif</label>
                                <select
                                    name="typeJustificatif"
                                    value={formData.typeJustificatif}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Aucun</option>
                                    {typeJustificatifOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="pcField">
                                <label>Niveau de validation *</label>
                                <div className="pcRadioGroup">
                                    {workflowOptions.map((option) => (
                                        <label key={option.value} className="pcRadioLabel">
                                            <input
                                                type="radio"
                                                name="workflowLevel"
                                                value={option.value}
                                                checked={formData.workflowLevel === option.value}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pcField">
                                <label>Justificatif obligatoire</label>
                                <div className="pcCheckRow">
                                    <input
                                        type="checkbox"
                                        name="obligatoire"
                                        checked={formData.obligatoire}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    <span>Oui</span>
                                </div>
                            </div>

                            <div className="pcField">
                                <label>Remboursement anticipé autorisé</label>
                                <div className="pcCheckRow">
                                    <input
                                        type="checkbox"
                                        name="remboursementAnticipe"
                                        checked={formData.remboursementAnticipe}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    <span>Oui</span>
                                </div>
                            </div>

                            {isEdit && (
                                <div className="pcField">
                                    <label>Actif</label>
                                    <div className="pcCheckRow">
                                        <input
                                            type="checkbox"
                                            name="actif"
                                            checked={formData.actif}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                        <span>Oui</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pcModal__footer">
                            <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal}
                                    disabled={loading}>
                                Annuler
                            </button>
                            <button className="pcBtn pcBtn--primary" type="button" onClick={handleSubmit}
                                    disabled={loading}>
                                {isEdit ? 'Enregistrer' : 'Ajouter'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des avances & prêts</h1>
                    <p>Configurez les types d'avances et prêts, conditions d'éligibilité et modalités de remboursement.</p>
                    {error && <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 700 }}>Erreur: {error}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`pcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    type="button"
                                >
                                    <Icon size={16} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="pcBody">
                        {/* AVANCES & PRÊTS */}
                        {activeTab === "avances" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Types d'avances et prêts paramétrables</h2>
                                    <button
                                        className="pcBtn pcBtn--primary"
                                        type="button"
                                        onClick={() => openModal()}
                                        disabled={loading}
                                    >
                                        <Plus size={16} /> Ajouter
                                    </button>
                                </div>

                                <div className="pcTableWrap">
                                    <table className="pcTable">
                                        <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Catégorie</th>
                                            <th>Ancienneté min.</th>
                                            <th>Mensualités</th>
                                            <th>Taux</th>
                                            <th>Mode retenue</th>
                                            <th>Justificatif</th>
                                            <th>Remb. anticipé</th>
                                            <th>Statut</th>

                                            <th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={10} className="pcEmptyCell">
                                                    Chargement...
                                                </td>
                                            </tr>
                                        ) : (avances || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="pcEmptyCell">
                                                    Aucune avance/prêt configuré(e)
                                                </td>
                                            </tr>
                                        ) : (
                                            (avances || []).map((avance) => (
                                                <tr key={avance.id}>
                                                    <td className="pcStrong">{avance.type}</td>
                                                    <td>
                                                        <span className="pcBadge" style={{
                                                            backgroundColor: avance.categorie === 'avance' ? '#10b981' : '#3b82f6'
                                                        }}>
                                                            {getCategorieLabel(avance.categorie)}
                                                        </span>
                                                    </td>
                                                    <td className="pcMuted">
                                                        {avance.ancienneteMinimale ? (
                                                            <span className="pcInline">
                                                                <Calendar size={14} className="pcAccent" />
                                                                {avance.ancienneteMinimale} mois
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td className="pcMuted">
                                                        {avance.nbMensualiteRemboursement || '-'}
                                                    </td>
                                                    <td className="pcMuted">
                                                        {avance.tauxInteret ? (
                                                            <span className="pcInline">
                                                                <Percent size={14} className="pcAccent" />
                                                                {avance.tauxInteret}%
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="pcCode" style={{ backgroundColor: '#204385' }}>
                                                            {avance.modeRetenue === 'autre' && avance.autreModeRetenue
                                                                ? avance.autreModeRetenue
                                                                : getModeRetenueLabel(avance.modeRetenue)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {avance.typeJustificatif ? (
                                                            <span className="pcInline">
                                                                <FileText size={14} className="pcAccent" />
                                                                {avance.typeJustificatif}
                                                                {avance.obligatoire && ' *'}
                                                            </span>
                                                        ) : (
                                                            <span className="pcMuted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {avance.remboursementAnticipe ? (
                                                            <CheckCircle size={18} className="pcOk" />
                                                        ) : (
                                                            <X size={18} className="pcOff" />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`pcPill ${avance.actif ? 'is-on' : 'is-off'}`}
                                                            type="button"
                                                            onClick={() => toggleActif(avance)}
                                                            disabled={loading}
                                                        >
                                                            {avance.actif ? 'Actif' : 'Inactif'}
                                                        </button>
                                                    </td>
                                                    <td className="is-right">
                                                        <div className="pcRowActions">
                                                            <button
                                                                className="pcIconBtn"
                                                                type="button"
                                                                title="Modifier"
                                                                onClick={() => openModal(avance)}
                                                                disabled={loading}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button
                                                                className="pcIconBtn pcIconBtn--danger"
                                                                type="button"
                                                                title="Supprimer"
                                                                onClick={() => handleDelete(avance.id)}
                                                                disabled={loading}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* PLAFONDS */}
                        {activeTab === "plafonds" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Gestion des plafonds</h2>
                                </div>

                                <div className="pcEmptyBox">
                                    Section des plafonds à venir...
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {renderModal()}
        </div>
    );
};

export default ParamAvances;